import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageSquare, X, Send, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

const GREETING =
  "Hi, I'm Carney — your Reverence Autos concierge. I can compare our details, price out your exact vehicle, or book a mobile detail right here in the chat. What are you driving?";

const SUGGESTIONS = [
  "What's the difference between Full and Ceramic?",
  "How much for an SUV interior?",
  "I'd like to book a detail",
];

function newSessionId() {
  return `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(newSessionId);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [booked, setBooked] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 350);
  }, [open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }]);
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/chat", { session_id: sessionId, message });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.booked) setBooked(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "I couldn't reach the studio just now — please try again.";
      setMessages((m) => [...m, { role: "assistant", content: msg }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <motion.button
        data-testid="assistant-launcher"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close assistant" : "Chat with Carney"}
        className="fixed bottom-5 right-5 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-brand-red text-white shadow-[0_18px_40px_-12px_rgba(200,16,46,0.7)] transition-colors hover:bg-brand-deep-red sm:bottom-7 sm:right-7"
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <X className="h-6 w-6" />
            </motion.span>
          ) : (
            <motion.span key="chat" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <MessageSquare className="h-6 w-6" />
            </motion.span>
          )}
        </AnimatePresence>
        {!open && (
          <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-brand-red/40" style={{ animationDuration: "2.6s" }} />
        )}
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="assistant-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 z-[60] flex h-[70vh] max-h-[560px] w-[calc(100vw-2rem)] max-w-[400px] flex-col overflow-hidden rounded-3xl border border-brand-silver/40 bg-white shadow-[0_40px_90px_-30px_rgba(26,28,30,0.55)] sm:bottom-28 sm:right-7"
            data-lenis-prevent
          >
            {/* Header */}
            <div className="flex items-center gap-3 bg-brand-charcoal px-5 py-4 text-white">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-red font-serif text-lg font-bold">C</div>
              <div className="min-w-0">
                <p className="font-serif text-base font-bold leading-tight">Carney</p>
                <p className="flex items-center gap-1.5 text-xs text-brand-silver">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Reverence concierge · online
                </p>
              </div>
              <button data-testid="assistant-close" onClick={() => setOpen(false)} className="ml-auto text-white/70 transition-colors hover:text-white" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} data-testid="assistant-messages" className="flex-1 space-y-3 overflow-y-auto bg-brand-offwhite px-4 py-5">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`assistant-msg-${m.role}`}
                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "rounded-br-md bg-brand-red text-white"
                        : "rounded-bl-md border border-brand-silver/40 bg-white text-brand-charcoal"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-md border border-brand-silver/40 bg-white px-4 py-3">
                    {[0, 1, 2].map((d) => (
                      <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full bg-brand-muted" style={{ animationDelay: `${d * 0.15}s` }} />
                    ))}
                  </div>
                </div>
              )}

              {booked && (
                <div className="flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700" data-testid="assistant-booked-note">
                  <CheckCircle2 className="h-4 w-4" /> Booking confirmed — our crew will text you to lock the window.
                </div>
              )}

              {messages.length === 1 && !loading && (
                <div className="space-y-2 pt-1">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid="assistant-suggestion"
                      onClick={() => send(s)}
                      className="block w-full rounded-xl border border-brand-silver/50 bg-white px-3 py-2.5 text-left text-sm text-brand-charcoal transition-colors hover:border-brand-red hover:text-brand-red"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="border-t border-brand-silver/40 bg-white p-3">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  data-testid="assistant-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  rows={1}
                  placeholder="Ask about pricing, services, or book…"
                  className="max-h-28 flex-1 resize-none rounded-2xl border border-brand-silver/50 bg-brand-offwhite px-4 py-2.5 text-sm text-brand-charcoal placeholder:text-brand-muted focus:border-brand-red focus:outline-none"
                />
                <button
                  data-testid="assistant-send"
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-red text-white transition-colors hover:bg-brand-deep-red disabled:opacity-40"
                  aria-label="Send"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
              <p className="mt-2 text-center text-[0.65rem] text-brand-muted">Carney can book you in — powered by Reverence Autos</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
